import {
  onAuthStateChanged,
  signInWithEmailAndPassword,
  signOut,
  updatePassword
} from 'firebase/auth';
import { doc, getDoc, updateDoc } from 'firebase/firestore';
import React, { createContext, useContext, useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { auth, db } from '../firebase_config';


const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [users, setUsers] = useState([]);
  const [notifications, setNotifications] = useState([]);

  // Demo users for testing
  const demoUsers = [
    {
      id: "1",
      name: "John Smith",
      email: "owner@versai.com",
      role: "owner",
      department: "Executive",
      joinDate: "2023-01-15",
      password: "demo123",
      phone: "+1 (555) 123-4567",
      bio: "Founder and CEO of Versai HR. Passionate about building great teams.",
      status: "active",
      lastPasswordChange: "2024-01-15T00:00:00Z"
    },
    {
      id: "2",
      name: "Sarah Johnson",
      email: "pm@versai.com",
      role: "project_manager",
      department: "Technology",
      joinDate: "2023-03-20",
      password: "demo123",
      phone: "+1 (555) 234-5678",
      bio: "Experienced project manager with 8+ years in tech industry.",
      status: "active",
      lastPasswordChange: "2024-02-20T00:00:00Z"
    },
    {
      id: "3",
      name: "Mike Davis",
      email: "hr@versai.com",
      role: "hr",
      department: "Human Resources",
      joinDate: "2023-02-10",
      password: "demo123",
      phone: "+1 (555) 345-6789",
      bio: "HR Manager focused on employee engagement and company culture.",
      status: "active",
      lastPasswordChange: "2024-01-10T00:00:00Z"
    },
    {
      id: "4",
      name: "Emily Chen",
      email: "employee@versai.com",
      role: "employee",
      department: "Development",
      joinDate: "2023-04-05",
      password: "demo123",
      phone: "+1 (555) 456-7890",
      bio: "Full-stack developer passionate about creating user-friendly applications.",
      status: "active",
      lastPasswordChange: "2024-03-05T00:00:00Z"
    },
    {
      id: "5",
      name: "Rakshit Verma",
      email: "rakshit@versai.in",
      role: "employee",
      department: "Development",
      joinDate: "2025-08-14",
      password: "demo123",
      phone: "+91 98765 43210",
      bio: "Software Developer with expertise in React and Node.js.",
      status: "active",
      lastPasswordChange: "2025-08-14T00:00:00Z"
    },
    {
      id: "6",
      name: "Deepak Sharma",
      email: "deepak@versai.in",
      role: "project_manager",
      department: "Technology",
      joinDate: "2025-08-14",
      password: "demo123",
      phone: "+91 87654 32109",
      bio: "Project Manager with strong leadership and technical skills.",
      status: "active",
      lastPasswordChange: "2025-08-14T00:00:00Z"
    },
    {
      id: "7",
      name: "Ankit Neeraj",
      email: "ankit@versai.in",
      role: "project_manager",
      department: "Technology",
      joinDate: "2025-08-14",
      password: "demo123",
      phone: "+91 76543 21098",
      bio: "Technical Project Manager specializing in agile methodologies.",
      status: "active",
      lastPasswordChange: "2025-08-14T00:00:00Z"
    },
    {
      id: "8",
      name: "Ankit Verma",
      email: "verma@versai.in",
      role: "employee",
      department: "Technology",
      joinDate: "2025-08-14",
      password: "demo123",
      phone: "+91 65432 10987",
      bio: "Backend developer focused on scalable API development.",
      status: "active",
      lastPasswordChange: "2025-08-14T00:00:00Z"
    }
  ];

  // FIXED: Initialize users synchronously to avoid timing issues
  const initializeUsers = () => {
    const storedUsers = localStorage.getItem('users');
    if (storedUsers) {
      try {
        const parsedUsers = JSON.parse(storedUsers);
        setUsers(parsedUsers);
        return parsedUsers;
      } catch (error) {
        console.error('Error parsing stored users:', error);
        setUsers(demoUsers);
        localStorage.setItem('users', JSON.stringify(demoUsers));
        return demoUsers;
      }
    } else {
      setUsers(demoUsers);
      localStorage.setItem('users', JSON.stringify(demoUsers));
      return demoUsers;
    }
  };

  // Initialize users and notifications
  useEffect(() => {
    // Initialize users immediately
    initializeUsers();

    // Load notifications
    const storedNotifications = localStorage.getItem('notifications');
    if (storedNotifications) {
      try {
        setNotifications(JSON.parse(storedNotifications));
      } catch (error) {
        console.error('Error parsing notifications:', error);
        setNotifications([]);
      }
    }
  }, []);

  const login = async (email, password) => {
    try {
      setLoading(true);
      
      // FIXED: Get current users from localStorage directly to avoid timing issues
      const currentUsers = users.length > 0 ? users : initializeUsers();
      
      // Check against stored users (including demo users)
      const user = currentUsers.find(u => u.email === email && u.password === password);

      if (user) {
        setCurrentUser(user);
        localStorage.setItem('currentUser', JSON.stringify(user));
        toast.success(`Welcome back, ${user.name}!`);
        return { user };
      }

      // Try Firebase auth for production
      const result = await signInWithEmailAndPassword(auth, email, password);
      
      // Get user data from Firestore
      const userDoc = await getDoc(doc(db, 'users', result.user.uid));
      if (userDoc.exists()) {
        const userData = {
          id: result.user.uid,
          ...result.user,
          ...userDoc.data()
        };
        setCurrentUser(userData);
        toast.success(`Welcome back, ${userData.name || userData.email}!`);
        return { user: userData };
      }

      return result;
    } catch (error) {
      console.error('Login error:', error);
      toast.error('Invalid email or password');
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const logout = async () => {
    try {
      setCurrentUser(null);
      localStorage.removeItem('currentUser');
      await signOut(auth);
      toast.success('Signed out successfully');
    } catch (error) {
      console.error('Logout error:', error);
      toast.error('Error signing out');
    }
  };

  const register = async (userData) => {
    try {
      setLoading(true);

      // Get current users
      const currentUsers = users.length > 0 ? users : initializeUsers();
      
      // Check if user already exists
      const existingUser = currentUsers.find(u => u.email === userData.email);
      if (existingUser) {
        throw new Error('User with this email already exists');
      }

      const newUser = {
        id: Date.now().toString(),
        ...userData,
        joinDate: new Date().toISOString().split('T')[0],
        status: 'active',
        lastPasswordChange: new Date().toISOString(),
        createdAt: new Date().toISOString()
      };

      const updatedUsers = [...currentUsers, newUser];
      setUsers(updatedUsers);
      localStorage.setItem('users', JSON.stringify(updatedUsers));

      toast.success(`User ${newUser.name} created successfully`);
      return { success: true, user: newUser };
    } catch (error) {
      console.error('Registration error:', error);
      toast.error(error.message);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  // FIXED: Enhanced changePassword function with better error handling
  const changePassword = async (newPassword) => {
    try {
      console.log('=== PASSWORD CHANGE DEBUG ===');
      console.log('Current User:', currentUser);
      console.log('Is Firebase User?:', !!auth.currentUser);
      console.log('Firebase User UID:', auth.currentUser?.uid);
      
      if (!currentUser) {
        throw new Error('No user logged in');
      }
  
      if (!newPassword || newPassword.length < 6) {
        throw new Error('Password must be at least 6 characters long');
      }
  
      // Check if this is a Firebase user
      const isFirebaseUser = auth.currentUser && auth.currentUser.uid === currentUser.id;
      console.log('Is Firebase User?:', isFirebaseUser);
  
      if (isFirebaseUser) {
        // FIREBASE USER: Update Firebase password directly
        console.log('Updating Firebase user password...');
        
        try {
          // FIXED: Use Firebase v9+ syntax
          await updatePassword(auth.currentUser, newPassword);
          console.log('Firebase password updated successfully');
  
          // Update Firestore user document if it exists
          try {
            await updateDoc(doc(db, 'users', auth.currentUser.uid), {
              lastPasswordChange: new Date().toISOString(),
              updatedAt: new Date().toISOString()
            });
            console.log('Firestore document updated');
          } catch (firestoreError) {
            console.warn('Firestore update failed (document may not exist):', firestoreError);
          }
  
          // Update local currentUser state
          const updatedCurrentUser = {
            ...currentUser,
            lastPasswordChange: new Date().toISOString(),
            updatedAt: new Date().toISOString()
          };
          setCurrentUser(updatedCurrentUser);
          localStorage.setItem('currentUser', JSON.stringify(updatedCurrentUser));
  
          // Add notification
          try {
            await addNotification({
              type: 'success',
              title: 'Password Changed',
              message: 'Your password has been updated successfully.',
              userId: currentUser.id
            });
          } catch (notificationError) {
            console.warn('Failed to add notification:', notificationError);
          }
  
          toast.success('Password changed successfully');
          return { success: true, message: 'Password changed successfully' };
  
        } catch (firebaseError) {
          console.error('Firebase password update failed:', firebaseError);
          
          // Handle specific Firebase errors
          if (firebaseError.code === 'auth/requires-recent-login') {
            toast.error('Please log out and log back in, then try changing your password again.');
            return { success: false, message: 'Please log out and log back in to change your password.' };
          }
          
          throw firebaseError;
        }
  
      } else {
        // DEMO USER: Update local users array
        console.log('Updating demo user password...');
        
        const currentUsers = users.length > 0 ? users : initializeUsers();
        
        // Find user by ID or email (case-insensitive)
        let targetUser = currentUsers.find(u => 
          u.id === currentUser.id || 
          (u.email && currentUser.email && u.email.toLowerCase() === currentUser.email.toLowerCase())
        );
        
        if (!targetUser) {
          console.log('Available users:', currentUsers.map(u => ({ id: u.id, email: u.email })));
          throw new Error(`Demo user not found. Email: ${currentUser.email}`);
        }
  
        console.log('Target demo user found:', targetUser.email);
  
        // Update the user's password in local array
        const updatedUsers = currentUsers.map(user =>
          user.id === targetUser.id
            ? {
                ...user,
                password: newPassword,
                lastPasswordChange: new Date().toISOString(),
                updatedAt: new Date().toISOString()
              }
            : user
        );
  
        // Update state and localStorage
        setUsers(updatedUsers);
        localStorage.setItem('users', JSON.stringify(updatedUsers));
  
        // Update currentUser
        const updatedCurrentUser = updatedUsers.find(u => u.id === targetUser.id);
        setCurrentUser(updatedCurrentUser);
        localStorage.setItem('currentUser', JSON.stringify(updatedCurrentUser));
  
        // Add notification
        try {
          await addNotification({
            type: 'success',
            title: 'Password Changed',
            message: 'Your password has been updated successfully.',
            userId: targetUser.id
          });
        } catch (notificationError) {
          console.warn('Failed to add notification:', notificationError);
        }
  
        console.log('Demo user password updated successfully');
        toast.success('Password changed successfully');
        return { success: true, message: 'Password changed successfully' };
      }
  
    } catch (error) {
      console.error('Error changing password:', error);
      toast.error(error.message);
      return { success: false, message: error.message };
    }
  };
  
  
  

  // FIXED: Employee password change function with syntax error corrected
  const changeEmployeePassword = async (employeeId, newPassword) => {
    try {
      if (!currentUser || currentUser.role !== 'owner') {
        throw new Error('Only owners can change employee passwords');
      }

      const currentUsers = users.length > 0 ? users : initializeUsers();
      const employee = currentUsers.find(u => u.id === employeeId);
      if (!employee) {
        throw new Error('Employee not found');
      }

      const updatedUsers = currentUsers.map(user =>
        user.id === employeeId
          ? {
              ...user,
              password: newPassword,
              lastPasswordChange: new Date().toISOString(),
              passwordChangedBy: currentUser.id,
              updatedAt: new Date().toISOString()
            }
          : user
      );

      setUsers(updatedUsers);
      localStorage.setItem('users', JSON.stringify(updatedUsers));

      // Add notification to employee
      try {
        await addNotification({
          type: 'info',
          title: 'Password Reset',
          message: `Your password has been reset by ${currentUser.name}. Please change it on your next login.`,
          userId: employeeId
        });

        // Add notification to owner
        await addNotification({
          type: 'success',
          title: 'Password Reset Complete',
          message: `Successfully reset password for ${employee.name}.`,
          userId: currentUser.id
        });
      } catch (notificationError) {
        console.warn('Failed to add notifications:', notificationError);
      }

      return { success: true, message: `Password reset successfully for ${employee.name}` };
    } catch (error) {
      console.error('Error changing employee password:', error);
      return { success: false, message: error.message };
    }
  };

  // Update user profile
  const updateProfile = async (profileData) => {
    try {
      if (!currentUser) {
        throw new Error('No user logged in');
      }

      const updatedUser = {
        ...currentUser,
        ...profileData,
        updatedAt: new Date().toISOString()
      };

      const currentUsers = users.length > 0 ? users : initializeUsers();
      const updatedUsers = currentUsers.map(user =>
        user.id === currentUser.id ? updatedUser : user
      );

      setUsers(updatedUsers);
      setCurrentUser(updatedUser);
      localStorage.setItem('users', JSON.stringify(updatedUsers));
      localStorage.setItem('currentUser', JSON.stringify(updatedUser));

      // Add success notification
      try {
        await addNotification({
          type: 'success',
          title: 'Profile Updated',
          message: 'Your profile information has been updated successfully.',
          userId: currentUser.id
        });
      } catch (notificationError) {
        console.warn('Failed to add notification:', notificationError);
      }

      // If using Firebase, update there too
      if (auth.currentUser) {
        try {
          await updateDoc(doc(db, 'users', auth.currentUser.uid), {
            ...profileData,
            updatedAt: new Date().toISOString()
          });
        } catch (firebaseError) {
          console.warn('Firebase update failed:', firebaseError);
        }
      }

      return { success: true, message: 'Profile updated successfully' };
    } catch (error) {
      console.error('Error updating profile:', error);
      return { success: false, message: error.message };
    }
  };

  // Add notification
  const addNotification = async (notificationData) => {
    try {
      const notification = {
        id: Date.now().toString() + Math.random().toString(36).substr(2, 9),
        ...notificationData,
        read: false,
        timestamp: new Date().toISOString()
      };

      const updatedNotifications = [notification, ...notifications];
      setNotifications(updatedNotifications);
      localStorage.setItem('notifications', JSON.stringify(updatedNotifications));

      return notification;
    } catch (error) {
      console.error('Error adding notification:', error);
      throw error;
    }
  };

  // Mark notification as read
  const markNotificationRead = async (notificationId) => {
    try {
      const updatedNotifications = notifications.map(notification =>
        notification.id === notificationId
          ? { ...notification, read: true, readAt: new Date().toISOString() }
          : notification
      );

      setNotifications(updatedNotifications);
      localStorage.setItem('notifications', JSON.stringify(updatedNotifications));
    } catch (error) {
      console.error('Error marking notification as read:', error);
    }
  };

  // Mark all notifications as read for current user
  const markAllNotificationsRead = async () => {
    try {
      if (!currentUser) return;

      const updatedNotifications = notifications.map(notification =>
        notification.userId === currentUser.id && !notification.read
          ? { ...notification, read: true, readAt: new Date().toISOString() }
          : notification
      );

      setNotifications(updatedNotifications);
      localStorage.setItem('notifications', JSON.stringify(updatedNotifications));
    } catch (error) {
      console.error('Error marking all notifications as read:', error);
    }
  };

  // Get notifications for current user
  const getUserNotifications = () => {
    if (!currentUser) return [];

    return notifications
      .filter(notification => notification.userId === currentUser.id)
      .sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));
  };

  // Get unread notification count for current user
  const getUnreadNotificationCount = () => {
    if (!currentUser) return 0;

    return notifications.filter(
      notification => notification.userId === currentUser.id && !notification.read
    ).length;
  };

  const migrateUserStatus = (users) => {
    return users.map(user => ({
      ...user,
      status: user.status || 'active' // Set default status if missing
    }));
  };

  useEffect(() => {
    // Check for stored user on app load
    const storedUser = localStorage.getItem('currentUser');
    if (storedUser) {
      try {
        const parsedUser = JSON.parse(storedUser);
        setCurrentUser(parsedUser);
      } catch (error) {
        console.error('Error parsing stored user:', error);
        localStorage.removeItem('currentUser');
      }
    }

    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (user && !storedUser) {
        try {
          const userDoc = await getDoc(doc(db, 'users', user.uid));
          if (userDoc.exists()) {
            const userData = {
              id: user.uid,
              ...user,
              ...userDoc.data()
            };
            setCurrentUser(userData);
            localStorage.setItem('currentUser', JSON.stringify(userData));
          }
        } catch (error) {
          console.error('Error fetching user data:', error);
        }
      }
      setLoading(false);
    });

    return unsubscribe;
  }, []);

  const value = {
    // User management
    currentUser,
    users,
    setUsers, // ADDED: Expose setUsers for external updates
    login,
    logout,
    register,
    loading,

    // Profile and password management
    changePassword,
    changeEmployeePassword,
    updateProfile,

    // Notifications
    notifications: getUserNotifications(),
    unreadNotificationCount: getUnreadNotificationCount(),
    addNotification,
    markNotificationRead,
    markAllNotificationsRead,

    // Demo users (for reference)
    demoUsers
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
